import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ShieldWarning, ArrowsClockwise, Eye, ArrowSquareOut, NotePencil, Sparkle, X, Check, Trash } from "@phosphor-icons/react";

export default function CORadar() {
  const [data, setData] = useState({ complaints: [], unseen_count: 0, open_count: 0 });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("unseen");
  const [sortBy, setSortBy] = useState("recent");
  const [drafts, setDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(false);
  const [reviewDraft, setReviewDraft] = useState(null);
  const [draftingId, setDraftingId] = useState(null);

  const loadDrafts = async () => {
    try { setDrafts((await api.get("/co-radar/drafts")).data.drafts || []); } catch (_) {}
  };
  useEffect(() => { loadDrafts(); }, []);

  const draftWo = async (complaintId) => {
    setDraftingId(complaintId);
    try {
      const r = await api.post(`/co-radar/complaints/${complaintId}/draft-wo`);
      if (r.data.already_existed) toast.info("Draft already exists — opening it");
      else toast.success("Draft created — AI wrote a starting point for you");
      setReviewDraft(r.data.draft);
      await loadDrafts();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Draft failed")); }
    setDraftingId(null);
  };

  const approve = async (draft, edits) => {
    try {
      const r = await api.post(`/co-radar/drafts/${draft._id}/approve`, edits);
      toast.success("Work Order created");
      setReviewDraft(null);
      await Promise.all([loadDrafts(), load()]);
      // If assigned, offer to open the job
      if (r.data.job_id) setTimeout(() => window.open(`/admin/jobs/${r.data.job_id}`, "_blank"), 400);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Approve failed")); }
  };

  const dismiss = async (draft) => {
    if (!window.confirm("Dismiss this draft? The source complaint stays unhandled.")) return;
    try {
      await api.delete(`/co-radar/drafts/${draft._id}`);
      toast.success("Draft dismissed");
      setReviewDraft(null);
      await loadDrafts();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Dismiss failed")); }
  };

  const load = async (f = filter) => {
    try { const r = await api.get(`/co-radar/complaints?filter=${f}`); setData(r.data); }
    catch { /* silent */ }
  };
  useEffect(() => { load(); }, [filter]);

  // Sort a copy — never mutate the state array
  const sorted = [...(data.complaints || [])].sort((a, b) => {
    const av = a.violation_date || "";
    const bv = b.violation_date || "";
    const ad = a.discovered_at || a.created_at || "";
    const bd = b.discovered_at || b.created_at || "";
    if (sortBy === "recent")     return bv.localeCompare(av);       // newest violation first
    if (sortBy === "oldest")     return av.localeCompare(bv);       // oldest violation first
    if (sortBy === "discovered") return bd.localeCompare(ad);       // most recently synced
    if (sortBy === "case")       return (a.casenumber || "").localeCompare(b.casenumber || "");
    return 0;
  });

  const sync = async () => {
    setBusy(true);
    try {
      toast.loading("Polling CARTO for every property…", { id: "co-sync" });
      const r = await api.post("/co-radar/sync", {}, { timeout: 120000 });
      toast.dismiss("co-sync");
      if (r.data.new_complaints > 0) toast.error(`🚨 ${r.data.new_complaints} new complaint${r.data.new_complaints === 1 ? "" : "s"} discovered`, { duration: 10000 });
      else toast.success(`All clear — no new complaints. Checked ${r.data.addresses_checked} addresses.`);
      load();
    } catch (e) { toast.dismiss("co-sync"); toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Sync failed")); }
    finally { setBusy(false); }
  };

  const markSeen = async (id) => {
    try { await api.post(`/co-radar/complaints/${id}/mark-seen`); load(); }
    catch { /* silent */ }
  };

  return (
    <AdminShell>
      <Toaster position="top-right" richColors closeButton/>
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-red-700 flex items-center gap-1">
            <ShieldWarning weight="fill" size={12}/> CO Radar
          </div>
          <h1 className="font-display font-black text-3xl mt-1">CO Radar · L&amp;I Sync</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            Weekly sync polls Philly's public CARTO violations table for every property in your portfolio. Any new casenumber = a fresh complaint you can act on <b>before</b> it becomes a violation notice.
          </p>
        </div>

        <div className="grid grid-cols-4 gap-3 max-w-2xl">
          <div className="rugged-card p-3">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Complaints tracked</div>
            <div className="font-black text-2xl mt-1">{data.complaints.length}</div>
          </div>
          <div className={`rugged-card p-3 ${data.unseen_count > 0 ? "bg-red-50 border-red-500" : ""}`}>
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">🚨 Unseen</div>
            <div className={`font-black text-2xl mt-1 ${data.unseen_count > 0 ? "text-red-700" : ""}`}>{data.unseen_count}</div>
          </div>
          <div className={`rugged-card p-3 ${data.open_count > 0 ? "bg-amber-50 border-amber-500" : ""}`}>
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Open cases</div>
            <div className={`font-black text-2xl mt-1 ${data.open_count > 0 ? "text-amber-800" : ""}`}>{data.open_count}</div>
          </div>
          <button
            onClick={() => setShowDrafts(v => !v)}
            data-testid="co-drafts-toggle"
            className={`rugged-card p-3 text-left ${drafts.length > 0 ? "bg-fuchsia-50 border-fuchsia-500" : ""}`}
          >
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">📝 WO Drafts</div>
            <div className={`font-black text-2xl mt-1 ${drafts.length > 0 ? "text-fuchsia-700" : ""}`}>{drafts.length}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-400">{showDrafts ? "hide" : "review"}</div>
          </button>
        </div>

        {showDrafts && drafts.length > 0 && (
          <div className="rugged-card p-3 bg-fuchsia-50 border-fuchsia-300" data-testid="co-drafts-panel">
            <div className="text-xs font-black uppercase tracking-widest text-fuchsia-800 mb-2 flex items-center gap-1">
              <Sparkle weight="fill" size={12}/> AI-drafted WOs ready for review
            </div>
            <div className="space-y-1.5">
              {drafts.map(d => (
                <button key={d._id} onClick={() => setReviewDraft(d)}
                  data-testid={`co-draft-row-${d._id}`}
                  className="w-full flex items-center gap-2 p-2 bg-white border border-fuchsia-200 hover:border-fuchsia-500 text-left">
                  <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
                    d.responsibility === "landlord" ? "bg-red-100 text-red-800" :
                    d.responsibility === "tenant" ? "bg-amber-100 text-amber-800" :
                    d.responsibility === "city" ? "bg-blue-100 text-blue-800" :
                    "bg-slate-100 text-slate-700"
                  }`}>{d.responsibility}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{d.title}</div>
                    <div className="text-[10px] text-slate-500 truncate">{d.property_address}{d.unit ? ` · ${d.unit}` : ""} · Case #{d.casenumber}</div>
                  </div>
                  <span className="text-[9px] font-black uppercase tracking-widest text-fuchsia-700">Review →</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button onClick={sync} disabled={busy} data-testid="co-sync-btn"
            className="h-11 px-4 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2 shadow-[3px_3px_0_#0f172a] disabled:opacity-50">
            <ArrowsClockwise weight="bold" size={14}/> {busy ? "Polling CARTO…" : "Sync now"}
          </button>
          <div className="flex gap-1">
            {["unseen","open","all"].map(f => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`co-filter-${f}`}
                className={`h-10 px-3 font-black uppercase text-[10px] tracking-widest border-2 ${filter===f?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-700 border-slate-300"}`}>
                {f}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Sort by</label>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              data-testid="co-sort-select"
              className="h-10 px-2 border-2 border-slate-300 bg-white font-semibold text-xs outline-none focus:border-red-500"
            >
              <option value="recent">Most recent violation</option>
              <option value="oldest">Oldest violation</option>
              <option value="discovered">Recently discovered</option>
              <option value="case">Case #</option>
            </select>
          </div>
        </div>

        {data.complaints.length === 0 ? (
          <div className="rugged-card p-8 text-center text-slate-500">
            {filter === "unseen" ? "No unseen complaints." : filter === "open" ? "No open cases." : "No complaints on record."}<br/>
            <span className="text-xs">Hit "Sync now" to poll the latest data from CARTO.</span>
          </div>
        ) : (
          <div className="rugged-card overflow-x-auto">
            <table className="w-full text-sm" data-testid="complaints-table">
              <thead className="bg-slate-100">
                <tr className="text-left">
                  <th className="p-2">Case #</th>
                  <th className="p-2">Address</th>
                  <th className="p-2">Violation</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Date</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(c => (
                  <tr key={c.id} className={`border-t border-slate-200 ${!c.seen ? "bg-red-50" : ""}`} data-testid={`complaint-${c.id}`}>
                    <td className="p-2 font-mono text-xs">{c.casenumber}</td>
                    <td className="p-2 max-w-[220px] truncate" title={c.property_address}>{c.property_address}{c.unit ? ` · ${c.unit}` : ""}</td>
                    <td className="p-2 max-w-[280px] truncate" title={c.title}>{c.title}</td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 text-white text-[9px] font-black uppercase tracking-widest ${c.is_open ? "bg-red-600" : "bg-slate-500"}`}>
                        {c.status || "—"}
                      </span>
                    </td>
                    <td className="p-2 font-mono text-xs">{(c.violation_date || "").slice(0,10)}</td>
                    <td className="p-2 text-right">
                      <div className="inline-flex gap-1 items-center">
                        <button onClick={() => draftWo(c.id)} disabled={draftingId === c.id}
                          data-testid={`draft-wo-${c.id}`}
                          className="text-fuchsia-600 hover:text-fuchsia-800 disabled:opacity-40" title="Draft a WO from this violation (AI suggests fields — you review)">
                          {draftingId === c.id ? <ArrowsClockwise size={14} className="animate-spin"/> : <NotePencil size={14}/>}
                        </button>
                        <a href={c.atlas_url} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-blue-700" title="Open in Atlas">
                          <ArrowSquareOut size={14}/>
                        </a>
                        {!c.seen && (
                          <button onClick={() => markSeen(c.id)} data-testid={`mark-seen-${c.id}`} className="text-slate-500 hover:text-emerald-700" title="Mark seen">
                            <Eye size={14}/>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {reviewDraft && <DraftReviewModal draft={reviewDraft} onClose={() => setReviewDraft(null)} onApprove={approve} onDismiss={dismiss}/>}
    </AdminShell>
  );
}


function DraftReviewModal({ draft, onClose, onApprove, onDismiss }) {
  const [title, setTitle] = useState(draft.title || "");
  const [category, setCategory] = useState(draft.category || "General Maintenance");
  const [priority, setPriority] = useState(draft.priority || "normal");
  const [description, setDescription] = useState(draft.description || "");

  const respColor = draft.responsibility === "landlord" ? "border-red-500 bg-red-50 text-red-900" :
                    draft.responsibility === "tenant" ? "border-amber-500 bg-amber-50 text-amber-900" :
                    draft.responsibility === "city" ? "border-blue-500 bg-blue-50 text-blue-900" :
                    "border-slate-400 bg-slate-100 text-slate-800";

  return (
    <div className="fixed inset-0 z-[9000] bg-black/40 grid place-items-center p-3 sm:p-6 overflow-auto" onClick={onClose} data-testid="draft-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-2xl rounded-lg border-2 border-slate-300 shadow-2xl">
        <div className="p-4 border-b-2 border-slate-200 flex items-start gap-2">
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 flex items-center gap-1">
              <Sparkle weight="fill" size={11}/> AI-drafted Work Order · Review before creating
            </div>
            <h2 className="mt-1 font-display font-black text-xl">Case #{draft.casenumber} · {draft.property_address}</h2>
          </div>
          <button onClick={onClose} data-testid="draft-close" className="text-slate-500 hover:text-black"><X size={20}/></button>
        </div>

        <div className="p-4 space-y-3">
          <div className={`border-2 rounded p-2 ${respColor}`}>
            <div className="text-[10px] font-black uppercase tracking-widest">
              Likely responsibility: {draft.responsibility}
            </div>
            {draft.responsibility_reason && <div className="text-xs mt-0.5">{draft.responsibility_reason}</div>}
          </div>

          {draft.review_checklist?.length > 0 && (
            <div className="border-2 border-cyan-200 bg-cyan-50/50 rounded p-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800 mb-1">Verify before creating</div>
              <ul className="text-xs text-slate-800 list-disc ml-5 space-y-0.5">
                {draft.review_checklist.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} data-testid="draft-title"
              className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm outline-none focus:border-fuchsia-500"/>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Category</label>
              <input value={category} onChange={e => setCategory(e.target.value)} data-testid="draft-category"
                className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm outline-none focus:border-fuchsia-500"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)} data-testid="draft-priority"
                className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm bg-white outline-none">
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} data-testid="draft-description"
              rows={4} className="mt-1 w-full p-2 border-2 border-slate-300 text-sm outline-none focus:border-fuchsia-500"/>
          </div>

          <a href={draft.atlas_url} target="_blank" rel="noreferrer" className="text-[10px] font-black uppercase tracking-widest text-blue-700 hover:underline">
            ↗ Open source violation on Atlas
          </a>
        </div>

        <div className="p-4 border-t-2 border-slate-200 flex items-center gap-2">
          <button onClick={() => onDismiss(draft)} data-testid="draft-dismiss"
            className="h-10 px-3 bg-white border-2 border-red-300 text-red-700 hover:bg-red-50 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
            <Trash weight="bold" size={12}/> Dismiss
          </button>
          <div className="flex-1"/>
          <button onClick={onClose} data-testid="draft-cancel"
            className="h-10 px-3 bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50 font-black uppercase tracking-widest text-[10px]">
            Cancel
          </button>
          <button onClick={() => onApprove(draft, { title, category, priority, description })} data-testid="draft-approve"
            className="h-10 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
            <Check weight="bold" size={12}/> Create Work Order
          </button>
        </div>
      </div>
    </div>
  );
}
